/**
 * Window Screen Kit Configurator – Shopify Widget Entry Point
 *
 * Self-contained widget for the "custom-window-screen-kit" Shopify product.
 * Same configurator as the assembled-screen widget, but renders an *exploded*
 * preview so the customer immediately understands they're buying a kit, not
 * a fully assembled screen.
 *
 * Pricing comes from the Shopify variant (read off the page price element by
 * the configurator's MutationObserver) — no kit-specific code overrides.
 *
 * Usage (Liquid):
 *   <div id="wsc-kit-configurator"></div>
 *   <script src="{{ 'wsc-kit-widget.iife.js' | asset_url }}" defer></script>
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './screen';
import './index.css';

const DEFAULTS = {
  containerSelector: '#wsc-kit-configurator',
  hideACO: true,
  kit: true,
};

const CFG = { ...DEFAULTS, ...(window.WSC_KIT_CONFIG || {}) };

// Surface kit context to screen.jsx's ACO lookup helpers so they pick up the
// kit's ACO field IDs from PRODUCT_FIELD_OVERRIDES.
if (typeof window !== 'undefined') {
  window.WSC_CONFIG = window.WSC_CONFIG || {};
  window.WSC_CONFIG.kit = true;
  window.WSC_CONFIG.productHandle = 'custom-window-screen-kit';
}

function init() {
  let root = document.querySelector(CFG.containerSelector);

  if (!root) {
    const form =
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('.product-form') ||
      document.querySelector('[data-product-form]');
    if (form) {
      root = document.createElement('div');
      root.id = 'wsc-kit-configurator';
      const btn =
        form.querySelector('[type="submit"]') ||
        form.querySelector('.add-to-cart') ||
        form.querySelector('button');
      if (btn) btn.parentNode.insertBefore(root, btn);
      else form.insertBefore(root, form.firstChild);
    }
  }

  if (!root) {
    console.error('[WSC-KIT] No container found – add <div id="wsc-kit-configurator"></div> to your template.');
    return;
  }

  console.log(`[WSC-KIT] Root font-size: ${getComputedStyle(document.documentElement).fontSize}`);

  if (CFG.hideACO) {
    const selectors = [
      '.wcpa_form_outer',
      '.wcpa-form-fields',
      '.aco-options',
      '.product-options',
      '[data-aco]',
    ];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        el.style.cssText =
          'position:absolute!important;left:-9999px!important;width:1px!important;height:1px!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;';
      });
    });
  }

  createRoot(root).render(
    <React.StrictMode>
      <App embedded={true} kit={true} />
    </React.StrictMode>,
  );

  console.log('[WSC-KIT] Window Screen Kit Configurator initialized');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

window.WSC_KIT = { init, config: CFG };
